'use client'

import { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react'
import { useRouter } from 'next/navigation'
import { trackLogoutRealizado } from '@/lib/analytics'

interface User {
  id: string
  email: string
  name: string | null
  avatarUrl: string | null
  tipoMEI: string | null
  cnpj: string | null
  plano: string
  role: string
  /** Créditos de comprovante limpo (compra avulsa). */
  comprovanteCredits?: number
  // Branding (Premium-only)
  logoUrl?: string | null
  nomeEmpresa?: string | null
  enderecoEmpresa?: string | null
  telefoneEmpresa?: string | null
  emailEmpresa?: string | null
  websiteEmpresa?: string | null
}

interface AuthContextType {
  user: User | null
  isLoading: boolean
  isSignedIn: boolean
  // signIn/signUp NAO redirecionam internamente. O caller (paginas /sign-in
  // e /sign-up usam router.push apos sucesso; o modal de auth apenas
  // fecha e mantem o usuario na pagina atual).
  signIn: (email: string, password: string) => Promise<{ error?: string; user?: User }>
  signUp: (email: string, password: string, name?: string) => Promise<{ error?: string }>
  signOut: () => Promise<void>
  refreshUser: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const router = useRouter()

  const refreshUser = useCallback(async () => {
    try {
      // Skip the request if no session indicator cookie exists to avoid 401 console errors
      const hasAuthCookie = document.cookie.split(';').some(c => c.trim().startsWith('has_session='))
      if (!hasAuthCookie) {
        setUser(null)
        setIsLoading(false)
        return
      }
      const res = await fetch('/api/auth/me')
      if (res.ok) {
        const data = await res.json()
        setUser(data.user)
      } else {
        setUser(null)
      }
    } catch {
      setUser(null)
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    refreshUser()
  }, [refreshUser])

  // Mantém o access_token (15min) vivo enquanto a aba está em uso, pra a pessoa
  // logada nunca esbarrar em 401 por sessão "expirada". Renova periodicamente e
  // ao voltar o foco. NÃO mexe no mecanismo de auth — só chama /api/auth/refresh.
  // navigator.locks + throttle em localStorage serializam entre abas: como o
  // refresh ROTACIONA o refresh_token, duas abas renovando juntas deslogariam.
  useEffect(() => {
    const THROTTLE_MS = 10 * 60 * 1000 // no máx. 1 renovação por 10min (todas as abas)

    const hasSession = () =>
      typeof document !== 'undefined' &&
      document.cookie.split(';').some((c) => c.trim().startsWith('has_session='))

    const keepAlive = async () => {
      if (!hasSession()) return
      const run = async () => {
        try {
          const last = Number(localStorage.getItem('cm_last_refresh') || 0)
          if (Date.now() - last < THROTTLE_MS) return
          localStorage.setItem('cm_last_refresh', String(Date.now()))
          await fetch('/api/auth/refresh', { method: 'POST' })
        } catch {
          /* silencioso — o retry no clique do checkout cobre o caso de borda */
        }
      }
      const nav = navigator as Navigator & {
        locks?: { request: (name: string, cb: () => unknown) => Promise<unknown> }
      }
      if (nav.locks?.request) {
        await nav.locks.request('cm-refresh', run)
      } else {
        await run()
      }
    }

    const onVisible = () => {
      if (document.visibilityState === 'visible') keepAlive()
    }

    const id = setInterval(keepAlive, 11 * 60 * 1000) // < 15min de TTL
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      clearInterval(id)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [])

  const signIn = async (email: string, password: string) => {
    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      })

      const data = await res.json()

      if (!res.ok) {
        return { error: data.error }
      }

      setUser(data.user)
      return { user: data.user }
    } catch {
      return { error: 'Erro de conexão' }
    }
  }

  const signUp = async (email: string, password: string, name?: string) => {
    try {
      const res = await fetch('/api/auth/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, name }),
      })

      const data = await res.json()

      if (!res.ok) {
        return { error: data.error }
      }

      setUser(data.user)
      return {}
    } catch {
      return { error: 'Erro de conexão' }
    }
  }

  const signOut = async () => {
    try {
      trackLogoutRealizado()
      await fetch('/api/auth/logout', { method: 'POST' })
    } finally {
      setUser(null)
      router.push('/')
    }
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isSignedIn: !!user,
        signIn,
        signUp,
        signOut,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

// Helper components for conditional rendering
export function SignedIn({ children }: { children: ReactNode }) {
  const { isSignedIn, isLoading } = useAuth()
  if (isLoading || !isSignedIn) return null
  return <>{children}</>
}

export function SignedOut({ children }: { children: ReactNode }) {
  const { isSignedIn, isLoading } = useAuth()
  if (isLoading || isSignedIn) return null
  return <>{children}</>
}
